import React, { useState, useEffect } from 'react';
import './Admin.css';

const PortfolioManager = () => {
    const [photos, setPhotos] = useState([]);
    const [settings, setSettings] = useState({
        title: 'Портфолио',
        columns: 3,
        gap: 20,
        showTitle: true
    });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [message, setMessage] = useState('');

    // ЗАГРУЖАЕМ ДАННЫЕ ПРИ ЗАГРУЗКЕ КОМПОНЕНТА
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Загружаем фото
            const photosRes = await fetch('/api/content/portfolio?field=photos');
            const photosData = await photosRes.json();

            // Загружаем настройки
            const settingsRes = await fetch('/api/content/portfolio?field=settings');
            const settingsData = await settingsRes.json();

            let allPhotos = [];
            if (photosData.data && Array.isArray(photosData.data)) {
                allPhotos = photosData.data;
            }

            // Сортируем по order
            allPhotos.sort((a, b) => (a.order || 0) - (b.order || 0));

            setPhotos(allPhotos);
            setSettings(settingsData.data || { title: 'Портфолио', columns: 3, gap: 20, showTitle: true });

            console.log('Загружено фото:', allPhotos.length);
        } catch (error) {
            console.error('Ошибка загрузки:', error);
            setMessage('❌ Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files);
        setUploading(true);

        for (const file of files) {
            const formData = new FormData();
            formData.append('photo', file);

            try {
                const response = await fetch('/api/upload/upload', {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    const data = await response.json();
                    // Добавляем новое фото и обновляем порядок
                    const newPhotos = [...photos, { ...data.photo, order: photos.length }];
                    setPhotos(newPhotos);
                    await savePhotos(newPhotos);
                    setMessage('✅ Фото загружено');
                    setTimeout(() => setMessage(''), 2000);
                }
            } catch (error) {
                console.error('Ошибка загрузки:', error);
                setMessage('❌ Ошибка загрузки файла');
            }
        }

        setUploading(false);
        e.target.value = '';
    };

    const handleDelete = async (id) => {
        if (!confirm('Удалить фото?')) return;

        try {
            const response = await fetch(`/api/upload/photo/${id}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                const newPhotos = photos.filter(p => p.id !== id);
                // Обновляем порядок
                const reorderedPhotos = newPhotos.map((p, idx) => ({ ...p, order: idx }));
                setPhotos(reorderedPhotos);
                await savePhotos(reorderedPhotos);
                setMessage('✅ Фото удалено');
                setTimeout(() => setMessage(''), 2000);
            }
        } catch (error) {
            console.error('Ошибка удаления:', error);
            setMessage('❌ Ошибка удаления');
        }
    };

    const toggleVisibility = async (id) => {
        const updatedPhotos = photos.map(p =>
            p.id === id ? { ...p, visible: !p.visible } : p
        );

        setPhotos(updatedPhotos);
        await savePhotos(updatedPhotos);
        setMessage('✅ Статус изменен');
        setTimeout(() => setMessage(''), 2000);
    };

    const savePhotos = async (photosToSave) => {
        try {
            await fetch('/api/content/portfolio', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ field: 'photos', data: photosToSave })
            });
        } catch (error) {
            console.error('Ошибка сохранения:', error);
        }
    };

    const saveSettings = async () => {
        try {
            await fetch('/api/content/portfolio', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ field: 'settings', data: settings })
            });
            setMessage('✅ Настройки сохранены!');
            setTimeout(() => setMessage(''), 2000);
        } catch (error) {
            console.error('Ошибка сохранения:', error);
            setMessage('❌ Ошибка сохранения настроек');
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (draggedItem === null) return;

        const newPhotos = [...photos];
        const [draggedPhoto] = newPhotos.splice(draggedItem, 1);
        newPhotos.splice(dropIndex, 0, draggedPhoto);

        // Обновляем порядок
        const reorderedPhotos = newPhotos.map((p, idx) => ({ ...p, order: idx }));
        setPhotos(reorderedPhotos);
        await savePhotos(reorderedPhotos);
        setDraggedItem(null);
        setMessage('✅ Порядок изменен');
        setTimeout(() => setMessage(''), 2000);
    };

    if (loading) {
        return <div className="loading-spinner">Загрузка портфолио...</div>;
    }

    const visiblePhotos = photos.filter(p => p.visible);
    const hiddenPhotos = photos.filter(p => !p.visible);

    return (
        <div className="portfolio-manager">
            <h2>📸 Портфолио</h2>

            {message && <div className="message success">{message}</div>}

            {/* Настройки */}
            <div className="settings-section">
                <h3>⚙️ Настройки блока</h3>
                <div className="settings-grid">
                    <div className="setting-item">
                        <label>Заголовок</label>
                        <input
                            type="text"
                            value={settings.title}
                            onChange={(e) => setSettings({...settings, title: e.target.value})}
                        />
                    </div>
                    <div className="setting-item">
                        <label>Колонки (2-4)</label>
                        <select
                            value={settings.columns}
                            onChange={(e) => setSettings({...settings, columns: Number(e.target.value)})}
                        >
                            <option value={2}>2 колонки</option>
                            <option value={3}>3 колонки</option>
                            <option value={4}>4 колонки</option>
                        </select>
                    </div>
                    <div className="setting-item checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={settings.showTitle}
                                onChange={(e) => setSettings({...settings, showTitle: e.target.checked})}
                            />
                            Показывать заголовок
                        </label>
                    </div>
                </div>
                <button onClick={saveSettings} className="save-settings-btn">
                    💾 Сохранить настройки
                </button>
            </div>

            {/* Загрузка фото */}
            <div className="upload-section">
                <h3>📤 Загрузить фото</h3>
                <label className={`upload-btn ${uploading ? 'uploading' : ''}`}>
                    📸 {uploading ? 'Загрузка...' : 'Выбрать фото'}
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleUpload}
                        disabled={uploading}
                        style={{ display: 'none' }}
                    />
                </label>
                <p className="hint">Поддерживаются: JPG, PNG, WEBP. Максимум 5MB</p>
            </div>

            {/* Активные фото */}
            <div className="photos-section">
                <h3>📷 Активные фото (перетаскивайте для сортировки)</h3>
                <div className="photos-grid active">
                    {visiblePhotos.map((photo, idx) => (
                        <div
                            key={photo.id}
                            className={`photo-item ${draggedItem === idx ? 'dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, idx)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, idx)}
                        >
                            <div className="drag-handle">⋮⋮</div>
                            <div className="photo-preview">
                                <img
                                    src={`${photo.url}`}
                                    alt="preview"
                                    onClick={() => setSelectedPhoto(photo)}
                                />
                                <div className="photo-overlay">
                                    <button onClick={() => toggleVisibility(photo.id)} className="visibility-btn" title="Скрыть">
                                        👁️
                                    </button>
                                    <button onClick={() => handleDelete(photo.id)} className="delete-btn" title="Удалить">
                                        🗑️
                                    </button>
                                </div>
                            </div>
                            <div className="photo-order">{idx + 1}</div>
                        </div>
                    ))}
                </div>
                {visiblePhotos.length === 0 && (
                    <div className="empty-state">Нет активных фото. Загрузите изображения!</div>
                )}
            </div>

            {/* Скрытые фото */}
            {hiddenPhotos.length > 0 && (
                <div className="photos-section hidden">
                    <h3>🙈 Скрытые фото</h3>
                    <div className="photos-grid">
                        {hiddenPhotos.map((photo) => (
                            <div key={photo.id} className="photo-item hidden">
                                <div className="photo-preview">
                                    <img
                                        src={`${photo.url}`}
                                        alt="preview"
                                        onClick={() => setSelectedPhoto(photo)}
                                        style={{ filter: 'grayscale(0.5)' }}
                                    />
                                    <div className="photo-overlay">
                                        <button onClick={() => toggleVisibility(photo.id)} className="visibility-btn" title="Показать">
                                            👁️‍🗨️
                                        </button>
                                        <button onClick={() => handleDelete(photo.id)} className="delete-btn" title="Удалить">
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Предпросмотр */}
            <div className="preview-section">
                <h3>🖼️ Предпросмотр Брендов</h3>
                <div className="site-preview">
                    {settings.showTitle && <h4>{settings.title}</h4>}
                    <div
                        className="preview-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.min(settings.columns, visiblePhotos.length || 1)}, 1fr)`,
                            gap: `${settings.gap}px`
                        }}
                    >
                        {visiblePhotos.slice(0, 6).map(photo => (
                            <div key={photo.id} className="preview-item">
                                <img src={`${photo.url}`} alt="" />
                            </div>
                        ))}
                    </div>
                    {visiblePhotos.length > 6 && (
                        <div className="preview-more">+ ещё {visiblePhotos.length - 6} фото</div>
                    )}
                </div>
            </div>

            {/* Модальное окно */}
            {selectedPhoto && (
                <div className="modal-overlay" onClick={() => setSelectedPhoto(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setSelectedPhoto(null)}>✕</button>
                        <img src={`${selectedPhoto.url}`} alt="" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioManager;